import React, { useState, useEffect } from "react";
import { Calendar, momentLocalizer, Views } from "react-big-calendar";
import moment from "moment-timezone";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import queryString from "query-string";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { KONTRA_BLUE } from "../../../styles/colors";

import {
  queryPatrols,
  queryIncidentReports,
  queryInstances,
} from "../../../utils/appsyncOperations";
import TimelineChip from "../../../components/Chips/Timeline";
import Header from "../../../components/Typography/Header";
import { appsyncError } from "../../../utils/errors";
import AcivityReportDialog from "../../../components/Dialogs/ActivityReport";
import IncidentReportDialog from "../../../components/Dialogs/IncidentReport";
import ResponseDialog from "../../../components/Dialogs/Response";

const styles = {
  root: {
    height: "100%",
  },
  calendar: {
    width: "100%",
  },
  progress: {
    height: 4,
  },
  legend: {
    display: "flex",
    justifyContent: "flex-start",
    flexWrap: "wrap",
  },
  calendarChip: {
    width: "100%",
  },
  chipLegend: {
    marginRight: 8,
    marginBottom: 8,
  },
  legendHeader: {
    marginBottom: 8,
  },
};

const localizer = momentLocalizer(moment);

const initRange = (timezone) => ({
  start: moment().tz(timezone).startOf("month").startOf("week"),
  end: moment().tz(timezone),
});

const PropertyTimeline = ({ propertyId, property, history, location }) => {
  const timezone = property && property.place.timezone;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [date, setDate] = useState(new Date());
  const [range, setRange] = useState(() => initRange(timezone));
  const [events, setEvents] = useState([]);
  const [activityReportId, setActivityReportId] = useState(null);
  const [incidentId, setIncidentId] = useState(null);
  const [responseId, setResponseId] = useState(null);

  const [view, setView] = useState(Views.MONTH);

  useEffect(() => {
    setLoading(true);
    setError("");
    const promiseArray = [];
    const params = {
      propertyId,
      from: range.start.valueOf(),
      to: range.end.valueOf(),
    };

    promiseArray.push(queryPatrols(params));

    promiseArray.push(queryIncidentReports(params));

    promiseArray.push(queryInstances(params));

    Promise.all(promiseArray)
      .then((results) => {
        const activities = results[0];
        const incidents = results[1];
        const responses = results[2];
        const events = [];

        for (const activity of activities) {
          events.push({
            color:
              activity.isVacationWatch || activity.isCardinal
                ? "goldenrod"
                : KONTRA_BLUE,
            end: new Date(activity.endTime),
            id: activity.patrolId,
            onClick: () => setActivityReportId(activity.patrolId),
            start: new Date(activity.startTime),
            title:
              activity.isVacactionWatch || activity.isCardinal
                ? "VIP Report"
                : "Daily Activity Report",
          });
        }
        for (const incident of incidents) {
          const endTime = incident.endTime
            ? incident.endTime
            : incident.startTime;
          events.push({
            color: "rebeccapurple",
            end: new Date(endTime),
            id: incident.incidentId,
            onClick: () => setIncidentId(incident.incidentId),
            start: new Date(incident.startTime),
            title: "Incident Report",
          });
        }
        for (const response of responses) {
          events.push({
            color: "crimson",
            end: new Date(response.createdAt),
            id: response.instanceId,
            onClick: () => setResponseId(response.instanceId),
            start: new Date(response.createdAt),
            title: "Response",
          });
        }
        setEvents(events);
        setLoading(false);
      })
      .catch((e) => {
        setError(appsyncError(e));
        setLoading(false);
      });
  }, [range, propertyId]);

  const onRangeChange = (range) => {
    let start;
    let end;

    if (Array.isArray(range)) {
      start = moment(range[0]).tz(timezone).startOf("day");
      end = moment(range[range.length - 1])
        .tz(timezone)
        .endOf("day");
    } else {
      start = moment(range.start);
      end = moment(range.end);
    }

    setRange({
      end,
      start,
    });
  };
  return (
    <div style={styles.root}>
      {loading ? (
        <LinearProgress style={styles.progress} />
      ) : (
        <div style={styles.progress} />
      )}
      {error !== "" && (
        <Typography variant="body1" component="p" color="error">
          {error}
        </Typography>
      )}
      <Header label="Legend" style={styles.legendHeader} />
      <div style={styles.legend}>
        <TimelineChip style={styles.chipLegend} variant="activity" legend />
        <TimelineChip style={styles.chipLegend} variant="cardinal" legend />
        <TimelineChip style={styles.chipLegend} variant="incident" legend />
        <TimelineChip style={styles.chipLegend} variant="response" legend />
      </div>
      <Calendar
        date={date}
        defaultView={Views.MONTH}
        eventPropGetter={
          view !== Views.AGENDA &&
          ((event) => ({
            style: { backgroundColor: event.color },
          }))
        }
        events={events}
        formats={{
          agendaTimeFormat: (date) => moment(date).tz(timezone).format("HH:mm"),
          agendaTimeRangeFormat: ({ end, start }) =>
            `${moment(start).tz(timezone).format("HH:mm")} — ${moment(end)
              .tz(timezone)
              .format("HH:mm")}`,
          eventTimeRangeEndFormat: (date) =>
            moment(date).tz(timezone).format("HH:mm"),
          eventTimeRangeFormat: ({ end, start }) =>
            `${moment(start).tz(timezone).format("HH:mm")} — ${moment(end)
              .tz(timezone)
              .format("HH:mm")}`,
          eventTimeRangeStartFormat: (date) =>
            moment(date).tz(timezone).format("HH:mm"),
          timeGutterFormat: "HH:mm",
        }}
        localizer={localizer}
        onNavigate={setDate}
        onRangeChange={onRangeChange}
        onSelectEvent={(event) => event.onClick()}
        onView={setView}
        popup={false}
        showMultiDayTimes
        style={styles.calendar}
        view={view}
      />
      {activityReportId !== null && (
        <AcivityReportDialog
          isOpen={activityReportId !== null}
          onClose={() => setActivityReportId(null)}
          reportId={activityReportId}
        />
      )}
      {incidentId !== null && (
        <IncidentReportDialog
          isOpen={incidentId !== null}
          onClose={() => setIncidentId(null)}
          reportId={incidentId}
        />
      )}
      <ResponseDialog
        isOpen={responseId !== null}
        onClose={() => setResponseId(null)}
        instanceId={responseId}
      />
    </div>
  );
};

const mapStateToProps = (state, ownProps) => ({
  property: state.properties.byId[ownProps.propertyId],
});

export default withRouter(connect(mapStateToProps)(PropertyTimeline));
